/**
 * Hook for managing transaction toolbar controls.
 * Provides computed toolbar state, mode switching, commit/rollback actions,
 * keyboard shortcuts, and pre-execution auto-begin logic.
 */

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import {
  commitTransaction,
  rollbackTransaction,
  toTransactionEngineType,
  TRANSACTION_SETTLE_IN_PROGRESS_CODE,
} from '../services/transactionApi';
import {
  beginTabTransaction,
  isTabTransactionBeginInFlight,
  TRANSACTION_BEGIN_DEADLINE_MS,
} from '../services/tabTransactionBegin';
import { TRANSACTION_TAB_IDLE_RESET, transactionEndedByFailedSettle } from './useTransactionSessionsReleased';
import { isQueryTab, type TransactionMode, type TransactionState, type TransactionIsolationLevel } from '../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { engineSupports } from '../../../../core/engines';
import { toast } from '../../../../components/ui/toaster';
import { classifyForSmartMode } from '../utils/smartModeClassifier';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import {
  registerKeybindingTarget,
  type KeybindingHandlersRef,
} from '../../../keybindings/dispatch/targetRegistry';

// ============================================================================
// Pure computation (exported for unit testing)
// ============================================================================

export type TransactionToolbarState = {
  /** Whether commit/rollback buttons should be visible */
  showCommitRollback: boolean;
  /** Whether the commit button is enabled */
  commitEnabled: boolean;
  /** Whether the rollback button is enabled */
  rollbackEnabled: boolean;
  /** Whether the mode toggle is disabled (active tx, a BEGIN in flight, or no connection) */
  modeChangeDisabled: boolean;
};

/**
 * State for the isolation level dropdown.
 */
export type IsolationLevelDropdownState = {
  /** Whether the dropdown is visible (hidden for auto mode or SQLite) */
  visible: boolean;
  /** Whether the dropdown is disabled (no connection) */
  disabled: boolean;
  /** Whether isolation level is changeable (always changeable, but shows info toast if active tx) */
  activeTransaction: boolean;
};

/**
 * Compute isolation level dropdown state from mode, state, engine, and disabled flag.
 */
export function computeIsolationLevelDropdownState(
  mode: TransactionMode,
  state: TransactionState,
  engineId: string,
  disabled: boolean,
): IsolationLevelDropdownState {
  const isSqlite = engineId === 'sqlite';
  return {
    visible: mode !== 'auto' && !isSqlite,
    disabled,
    activeTransaction: state === 'active' || state === 'error',
  };
}

/**
 * Compute toolbar button state from transaction mode, state, and disabled flag.
 */
export function computeTransactionToolbarState(
  mode: TransactionMode,
  state: TransactionState,
  disabled: boolean,
  /** A BEGIN is on the wire for the tab (`transactionBeginToken`). */
  beginPending = false,
): TransactionToolbarState {
  return {
    showCommitRollback: mode !== 'auto',
    commitEnabled: !disabled && state === 'active',
    rollbackEnabled: !disabled && (state === 'active' || state === 'error'),
    modeChangeDisabled: disabled || beginPending || state === 'active' || state === 'error',
  };
}

/**
 * Whether the engine has real transaction support. Drives both the toolbar
 * gate (hide the controls) and the pre-execution no-op: engines without it
 * (e.g. kusto/KQL, or engines whose transaction session is not yet wired) run
 * every statement in auto-commit and must never route through a begin.
 */
export function supportsTransactions(engineId: DatabaseEngineId): boolean {
  return engineSupports(engineId, 'queryExecution', 'supportsTransactions');
}

/**
 * Whether a transaction should be auto-begun before query execution.
 * Returns true when in manual mode and no transaction is active.
 */
export function shouldAutoBeginTransaction(
  mode: TransactionMode,
  state: TransactionState,
): boolean {
  return mode === 'manual' && state === 'idle';
}

/**
 * Whether smart mode should begin a transaction for the given SQL.
 * Returns true only when in smart mode, idle state, and the query contains DML.
 */
export function shouldSmartModeBeginTransaction(
  mode: TransactionMode,
  state: TransactionState,
  sql: string,
): boolean {
  if (mode !== 'smart' || state !== 'idle') return false;
  return classifyForSmartMode(sql) === 'dml';
}

/**
 * Does a Commit/Rollback response still belong to the transaction the tab
 * holds? `settled` is the generation captured when the op was sent; `current`
 * is what the tab holds when the response lands. A COMMIT can stay pending
 * past a reconnect's release deadline; the reconnect resets the tab and the
 * user begins a NEWER transaction — that response must not reset it (final
 * review, MAJOR). Both undefined (a legacy tab) is the same transaction.
 */
export function settlementOwnsTab(settled: number | undefined, current: number | undefined): boolean {
  return settled === current;
}

// ============================================================================
// Hook
// ============================================================================

interface UseTransactionControlProps {
  tabId: string;
  transactionMode: TransactionMode;
  transactionState: TransactionState;
  /** Identity of the session the tab holds (`beginTransaction`'s `sessionGeneration`). */
  transactionSessionGeneration?: number;
  /** A BEGIN is on the wire for the tab (`transactionBeginToken` is set). */
  transactionBeginPending?: boolean;
  isolationLevel?: TransactionIsolationLevel;
  connectionId?: string;
  database?: string;
  engineId?: DatabaseEngineId;
  disabled?: boolean;
  isActiveTab?: boolean;
}

export const TRANSACTION_KEYBINDING_COMMAND_IDS = [
  'query.commitTransaction',
  'query.rollbackTransaction',
] as const;

type UseTransactionKeybindingTargetOptions = {
  readonly tabId: string;
  readonly isActiveTab: boolean;
  readonly commitEnabled: boolean;
  readonly rollbackEnabled: boolean;
  readonly onCommit: () => void | Promise<void>;
  readonly onRollback: () => void | Promise<void>;
};

export function useTransactionKeybindingTarget({
  tabId,
  isActiveTab,
  commitEnabled,
  rollbackEnabled,
  onCommit,
  onRollback,
}: UseTransactionKeybindingTargetOptions): void {
  const handlersRef = useRef<KeybindingHandlersRef>({ current: {} });
  handlersRef.current.current = {
    'query.commitTransaction': onCommit,
    'query.rollbackTransaction': onRollback,
  };
  const shortcutStateRef = useRef({ isActiveTab, commitEnabled, rollbackEnabled });
  shortcutStateRef.current = { isActiveTab, commitEnabled, rollbackEnabled };
  useLayoutEffect(() => registerKeybindingTarget({
    scope: 'query-editor',
    tabId,
    handlers: handlersRef.current,
    canExecute: (commandId) => {
      if (!shortcutStateRef.current.isActiveTab) return false;
      if (commandId === 'query.commitTransaction') {
        return shortcutStateRef.current.commitEnabled;
      }
      if (commandId === 'query.rollbackTransaction') {
        return shortcutStateRef.current.rollbackEnabled;
      }
      return true;
    },
  }), [tabId]);
}

export function useTransactionControl({
  tabId,
  transactionMode,
  transactionState,
  transactionSessionGeneration,
  transactionBeginPending = false,
  isolationLevel,
  connectionId,
  database,
  engineId = 'mssql',
  disabled = false,
  isActiveTab = false,
}: UseTransactionControlProps) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);
  const { track } = useAnalytics();
  // Which of Commit / Rollback is on the wire. Both buttons (and both
  // shortcuts) are disabled while one is: the backend settles a session ONCE,
  // and a Rollback that landed behind an in-flight COMMIT used to be reported
  // as "rolled back" over data that had just committed (round-2 review).
  const [settling, setSettling] = useState<'commit' | 'rollback' | null>(null);
  const toolbarState = computeTransactionToolbarState(
    transactionMode, transactionState, disabled || settling !== null, transactionBeginPending,
  );

  const isolationLevelState = computeIsolationLevelDropdownState(
    transactionMode, transactionState, engineId, disabled,
  );

  const setTransactionMode = useCallback((mode: TransactionMode) => {
    if (transactionState === 'active' || transactionState === 'error') {
      toast.warning({
        title: 'Active transaction',
        description: 'Commit or rollback the current transaction before changing mode.',
      });
      return;
    }
    if (transactionBeginPending) {
      toast.warning({
        title: 'Transaction starting',
        description: 'Change the mode once the transaction has started.',
      });
      return;
    }
    if (mode === transactionMode) return;
    updateTabContent(tabId, { transactionMode: mode });
    track('feature:transaction_mode_change', {
      from: transactionMode,
      to: mode,
      engine: engineId,
    });
  }, [tabId, transactionMode, transactionState, transactionBeginPending, engineId, updateTabContent, track]);

  const setIsolationLevel = useCallback((level: TransactionIsolationLevel) => {
    updateTabContent(tabId, { isolationLevel: level });
    if (transactionState === 'active' || transactionState === 'error') {
      toast.default({
        title: 'Isolation level updated',
        description: 'Will apply to the next transaction.',
      });
    }
  }, [tabId, transactionState, updateTabContent]);

  const settle = useCallback(async (op: 'commit' | 'rollback') => {
    if (settling) return;
    setSettling(op);
    // The session this op is for, captured BEFORE the wire: the response is
    // applied only while the tab still holds it (`settlementOwnsTab`).
    const settledGeneration = transactionSessionGeneration;
    try {
      const result = await (op === 'commit' ? commitTransaction(tabId) : rollbackTransaction(tabId));
      const tab = useWorkspaceStore.getState().getTab(tabId);
      const current = tab && isQueryTab(tab) ? tab.content.transactionSessionGeneration : settledGeneration;
      if (!settlementOwnsTab(settledGeneration, current) && result.code !== TRANSACTION_SETTLE_IN_PROGRESS_CODE) {
        // A late response for a transaction this tab has since left (a
        // reconnect reset it and a newer one began): say what happened to the
        // EARLIER one, touch nothing — the newer transaction's controls stay.
        const outcome = result.success ? (op === 'commit' ? 'committed' : 'rolled back') : 'failed';
        toast.default({
          title: `Earlier ${op} finished: ${outcome}`,
          description: result.success
            ? 'It belonged to a transaction this tab has since left; the current transaction was not touched.'
            : `${result.error ?? 'Unknown error'} — it belonged to a transaction this tab has since left; the current transaction was not touched.`,
        });
        return;
      }
      if (result.success) {
        // Same end state as every other "transaction over" path — generation
        // cleared too, so a late release event for it cannot reset a newer one.
        updateTabContent(tabId, TRANSACTION_TAB_IDLE_RESET);
        toast.success({ title: op === 'commit' ? 'Transaction committed' : 'Transaction rolled back' });
      } else if (result.code === TRANSACTION_SETTLE_IN_PROGRESS_CODE) {
        // The other op is still settling the session (a shortcut that slipped
        // past the disabled button). Nothing ran; say which one is in flight.
        toast.warning({
          title: op === 'commit' ? 'Rollback in progress' : 'Commit in progress',
          description: result.error,
        });
      } else {
        // The backend has ended the session either way (Codex r7): no phantom `active`.
        const title = op === 'commit' ? 'Commit failed' : 'Rollback failed';
        if (tab && isQueryTab(tab)) {
          updateTabContent(tabId, transactionEndedByFailedSettle(tab.content, `${title}: ${result.error ?? 'unknown error'}`));
        }
        toast.error({ title, description: `${result.error ?? 'Unknown error'} — the transaction is no longer open.` });
      }
    } finally {
      setSettling(null);
    }
  }, [tabId, settling, transactionSessionGeneration, updateTabContent]);

  const handleCommit = useCallback(async () => {
    if (transactionState !== 'active') return;
    await settle('commit');
  }, [transactionState, settle]);

  const handleRollback = useCallback(async () => {
    if (transactionState !== 'active' && transactionState !== 'error') return;
    await settle('rollback');
  }, [transactionState, settle]);

  /**
   * Call before executing a query. In manual mode with idle state, this
   * auto-begins a transaction. In smart mode, classifies the SQL and
   * begins a transaction only for DML queries. Returns true if execution
   * should proceed, false if beginning the transaction failed.
   *
   * `writes: true` is a write with no SQL to classify — a results-grid Save —
   * which smart mode treats as DML, like the same UPDATE typed and executed
   * (owner decision 09-29: a grid Save in Smart mode starts a transaction).
   */
  const prepareForExecution = useCallback(async (sql?: string, options?: { writes?: boolean }): Promise<boolean> => {
    // Engines without transaction support (e.g. kusto/KQL) run in auto-commit —
    // there is nothing to begin, so proceed as a no-op. This also protects a
    // session-restored 'manual'/'smart' mode, or a connection switched to such
    // an engine mid-tab, from throwing in toTransactionEngineType below.
    if (!supportsTransactions(engineId)) {
      return true;
    }
    const needsManualBegin = shouldAutoBeginTransaction(transactionMode, transactionState);
    const needsSmartBegin = options?.writes
      ? transactionMode === 'smart' && transactionState === 'idle'
      : sql ? shouldSmartModeBeginTransaction(transactionMode, transactionState, sql) : false;

    if (!needsManualBegin && !needsSmartBegin) {
      return true;
    }
    if (!connectionId || !database) {
      return true; // let normal execution handle missing connection
    }
    const engineType = toTransactionEngineType(engineId);
    const lvl = isolationLevel === 'default' ? undefined : isolationLevel;
    // A second caller (Execute and a grid Save racing) joins the BEGIN in
    // flight; only the one that started it reports on it.
    const joined = isTabTransactionBeginInFlight(tabId);
    // Owns the tab while BEGIN is on the wire and re-checks it afterwards;
    // on success the tab is `active` with the session's generation, which
    // travels with every statement and Save and gates the release event.
    const outcome = await beginTabTransaction({
      tabId, connectionId, database, engineType, isolationLevel: lvl, mode: transactionMode,
    });
    if (outcome.status === 'refused') {
      if (!joined) toast.error({ title: 'Failed to begin transaction', description: outcome.error });
      return false;
    }
    if (outcome.status === 'abandoned') {
      // Given up on (deadline or Stop): the UI is free again and main rolls
      // back any late session in the background. Nothing runs; a retry starts
      // a fresh BEGIN.
      if (!joined) {
        toast.error({
          title: "Couldn't start a transaction — try again",
          description: outcome.reason === 'timeout'
            ? `The connection did not answer within ${TRANSACTION_BEGIN_DEADLINE_MS / 1000} s. Nothing was run.`
            : 'Stopped before the transaction started. Nothing was run.',
        });
      }
      return false;
    }
    if (outcome.status === 'superseded') {
      // Closed, or its mode / connection / database changed, while BEGIN was
      // on the wire: the late session was rolled back and released.
      if (!joined && useWorkspaceStore.getState().getTab(tabId)) {
        toast.default({
          title: 'Not run',
          description: 'The tab changed while its transaction was starting; nothing was run.',
        });
      }
      return false;
    }
    if (needsSmartBegin && !joined) {
      toast.default({
        title: 'Transaction started',
        description: 'DML detected — switched to manual transaction mode. Commit or rollback when ready.',
      });
    }
    return true;
  }, [tabId, transactionMode, transactionState, connectionId, database, engineId, isolationLevel]);

  useTransactionKeybindingTarget({
    tabId,
    isActiveTab,
    commitEnabled: toolbarState.commitEnabled,
    rollbackEnabled: toolbarState.rollbackEnabled,
    onCommit: handleCommit,
    onRollback: handleRollback,
  });

  return {
    ...toolbarState,
    /** Which of Commit / Rollback is currently on the wire, if any. */
    settling,
    isolationLevelState,
    setTransactionMode,
    setIsolationLevel,
    handleCommit,
    handleRollback,
    prepareForExecution,
  };
}
